import { parseSearchFiltersToUrl } from '~/apps/pu-links/infrastructure/searchUrlHelpers'
import { getTransformObject } from '~/common/photos/transforms'
import type { Logger } from '~/composables/useGtmLogRequest'
import { injectable } from '~/container'
import { getLangFallbackUrl } from '~/lang/utils/langfetch'
import { getWKTPointFromGeoPoint } from '~/utility/geo/GeoLocation'
import { getPointFromStringOrDefault } from '~/utility/geo/latLng'
import { keysToCamel } from '~/utility/toCamel'
import {
  CampsiteMapsSearchResult,
  MapSearchCampsitesResults,
  SearchFilters,
} from '../schemas'
import { formatBounds } from './bounds'
import { fetchWithCache } from '../../../utility/fetchWithCache'

// const SEARCH_RESULTS_LIMIT = 200

@injectable()
export class FetchCampsitePriceMapSearchService {
  async search(
    filters: SearchFilters,
    langCode: string,
    logger: Logger,
    fetchOptions?: any,
  ): Promise<MapSearchCampsitesResults> {
    const params = this.filtersToParams(filters).toString()
    const fieldsToShow = [
      'campsite_id',
      'categories',
      'category_ids',
      'hierarchy_text_short',
      'id',
      'name',
      'path',
      'cheapest_price',
      'point',
      'primary_photo',
      'rate_count',
      'rating',
      'rating100',
      'slug',
      'url',
    ]
    const url = getLangFallbackUrl(
      langCode,
      `/search2/price/?fields=${fieldsToShow.join(
        ',',
      )}&${params}&sort=magic&facet_counts=none&seo=0`,
    )

    const response: any = await fetchWithCache(url, fetchOptions)
    return {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- API response field
      bounds: response.bounding_box
        ? // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- API response field
          formatBounds(response.bounding_box)
        : null,
      campsites: this.getFilteredResults(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- API response field
        this.formatResults(keysToCamel(response.results)),
      ),
      pagination: {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- API response field
        currentPage: Number(response.page),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
        totalPages: response.num_pages,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
        totalCount: response.count,
      },
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
      title: response.title,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
      heading: response.heading,
      bookable: true,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
      category: response.category,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
      count: response.count,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
      path: response.path,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
      point: response.point,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
      leadPrice: response.lead_price,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
      sortOptions: response.sort_options,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
      sortOption: response.sort || response.sort_field,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
      maxPrice: response.result_max_price,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
      within: keysToCamel(response.within),
    }
  }

  private getFilteredResults(
    results: CampsiteMapsSearchResult[],
  ): CampsiteMapsSearchResult[] {
    return results.filter((r) => r.name !== 'noindex')
  }

  private formatResults(results: any[]): CampsiteMapsSearchResult[] {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- Map result typing
    return (
      results
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- API response field
        .filter((campsite) => !!campsite.point)

        .map((campsite) => {
          const point = getWKTPointFromGeoPoint(
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- API response field
            getPointFromStringOrDefault(campsite.point),
          )
          const { lat, lng } = getPointFromStringOrDefault(point)
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- Map result with spread
          return {
            ...campsite,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- API response field
            id: campsite.campsiteId.toString(),
            status: 'bookable',
            bookable: true,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
            price: campsite.cheapestPrice,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
            hierarchy: campsite.hierarchyTextShort,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- API response field
            rating: Number(campsite.rating),
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- API response field
            rating10: Number(campsite.rating) * 2,
            point,
            lat,
            lng,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- API response field
            photo: campsite.primaryPhoto
              ? {
                  url: getTransformObject(
                    'map',
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- API response field
                    campsite.primaryPhoto.url.masterImage,
                  ),
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- API response field
                  caption: campsite.primaryPhoto?.caption || '',
                }
              : null,
          }
        })
    )
  }

  private filtersToParams(filters: SearchFilters) {
    if (!filters.dates) {
      throw new Error('no dates')
    }
    const params = new URLSearchParams()
    for (const param of parseSearchFiltersToUrl({
      ...filters,
      // limit: SEARCH_RESULTS_LIMIT,
    })) {
      params.append(param.filterName, param.filterValue)
    }
    if (params.get('bounds')) {
      // remove bounds if path
      if (params.get('path')) {
        params.delete('bounds')
      }
    }
    if (filters.engine) {
      params.set('engine', filters.engine.toString())
    }
    return params
  }
}
